import { Box, Button, ButtonGroup, Card, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import 'quill/dist/quill.snow.css';
import { useEffect, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../../app/AppStyles';
import RichTextEditor from '../../subcomponents/RichTextEditor';
import { fetchInformationArticle, fetchInformationArticles, postInformationArticle, putInformationArticle, resetInformationArticleManagementStatus } from './informationManagementSlice';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: '40%' },
    maxHeight: '90%',
    boxShadow: 24,
};

export default function InformationEditCard({ handleClose, addNew, informationArticleId }) {
    const dispatch = useDispatch();
    const quillRef = useRef();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { informationArticle, informationArticlePost, informationArticlePut } = useSelector((state) => state.management.information);

    const [changed, setChanged] = useState(false);
    const [newInformationArticle, setNewInformationArticle] = useState({});

    // Put status callback
    useEffect(() => {
        if (informationArticlePut.status === "success") {
            dispatch(resetInformationArticleManagementStatus());
            dispatch(fetchInformationArticle(informationArticleId));
            handleClose();
        } else if (informationArticlePut.status === "failed") {
            dispatch(resetInformationArticleManagementStatus());
        };
    }, [dispatch, handleClose, informationArticlePut.status]);

    // Post status callback
    useEffect(() => {
        if (informationArticlePost.status === "success") {
            dispatch(resetInformationArticleManagementStatus());
            dispatch(fetchInformationArticles());
            handleClose();
        } else if (informationArticlePost.status === "failed") {
            dispatch(resetInformationArticleManagementStatus());
        };
    }, [dispatch, handleClose, informationArticlePost.status]);

    // Selected period state update 
    useEffect(() => {
        if (!addNew && informationArticle.data) {
            setNewInformationArticle(informationArticle.data);
        }
    }, [informationArticle.data, addNew]);

    // Keep track of changed state 
    useEffect(() => {
        if (newInformationArticle !== informationArticle.data) {
            setChanged(true);
        } else {
            setChanged(false);
        };
    }, [informationArticle.data, newInformationArticle]);

    // Update click function (check property character limits etc.)
    const updateClick = () => {
        let tempObject = { ...newInformationArticle }; // Needed to circumvent weird read only property error

        tempObject.modifiedBy = `AP/${user.number}`;

        dispatch(putInformationArticle(tempObject));
    };

    // Add click function (check property character limits etc.)
    const addClick = () => {
        let tempObject = { ...newInformationArticle }; // Needed to circumvent weird read only property error

        tempObject.createdBy = `AP/${user.number}`;

        dispatch(postInformationArticle(tempObject));
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.NEWS} {addNew ? resourceFinal[appLanguage].words.ADD.toLowerCase() : resourceFinal[appLanguage].words.EDIT.toLowerCase()}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth required
                                defaultValue={informationArticle.data?.title}
                                label={resourceFinal[appLanguage].words.TITLE}
                                onChange={(event) => setNewInformationArticle({
                                    ...newInformationArticle,
                                    title: event.target.value
                                })}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <FormControlLabel control={
                                <Checkbox
                                    defaultChecked={informationArticle.data?.isPublished}
                                    onChange={(event) => setNewInformationArticle({
                                        ...newInformationArticle,
                                        isPublished: event.target.checked ? true : false
                                    })} />
                            } label={resourceFinal[appLanguage].words.PUBLISHED} />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <TextField
                                fullWidth type="number"
                                defaultValue={informationArticle.data?.number}
                                onChange={(event) => setNewInformationArticle({
                                    ...newInformationArticle,
                                    number: event.target.value
                                })}
                                variant="standard"
                                helperText={resourceFinal[appLanguage].words.NUMBER}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <RichTextEditor
                                ref={quillRef}
                                defaultValue={informationArticle.data?.content || ''}
                                onTextChange={(html) => setNewInformationArticle({
                                    ...newInformationArticle,
                                    content: html
                                })}
                            />
                        </Grid>
                        {(informationArticlePost.data === false || informationArticlePut.data === false) && <Grid size={{ xs: 12 }}>
                            <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}</Typography>
                        </Grid>}
                        <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <ButtonGroup sx={{ justifyContent: 'center' }}>
                                <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                                {!addNew && <Button disabled={!changed} variant="outlined" color="primary" onClick={updateClick}>{resourceFinal[appLanguage].words.SAVE}</Button>}
                                {addNew && <Button variant="outlined" color="primary" onClick={addClick}>{resourceFinal[appLanguage].words.ADD}</Button>}
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </Scrollbars>
            </Card>
        </Box>
    );
}
